import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { formValueSelector, change } from 'redux-form';
import { setMortgageSubmissionId, mortgageSubmission } from '../../../../../reducers/mortgage';
import { getToken } from '../../../../../reducers/auth';
import styles from './style.module.css';
import defaultCoachImage from '../../../../../assets/images/casey-fraser.jpg';
import * as Routes from '../../../../constants/routes';
import { float, numberWithCommas, logger, appendQueryParams } from '../../../../helpers';
import { calculateMaxMortgage, calculatePaymentFromPrincipal, formatMortgageTerm } from '../../../../helpers/mortgage';
import { trackingEvent } from '../../../../helpers/tracking';
import InfoBox from '../../../../common/components/info-box';
import SummaryAnswers from '../../components/forms/summary';
import { getTeamMember } from '../../../../../reducers';

const ctaButton = {
    link: 'https://libro.coconutcalendar.com/service',
    label: 'Book A Meeting',
    target: '_blank',
};

const callButton = {
    link: 'tel:18003618222',
    label: 'Call Now',
    target: '_self',
};

const tooltip = {
    title: 'Did you know?',
    content: 'A general affordability rule is that your monthly housing costs should not exceed 32% of your gross household monthly income. Try some different numbers; if you are exceeding this amount, be sure to talk with your Libro Coach.',
    button: ctaButton
};

const frequencySuffixMap = {
    'Weekly': 'wk',
    'Weekly (Accelerated)': 'wk',
    'Bi-Weekly': 'bi-wk',
    'Bi-Weekly (Accelerated)': 'bi-wk',
    'Semi-Monthly': 'semi-mo',
    'Monthly': 'mo'
};

const paymentFrequencyNumMap = {
    'Weekly': 52,
    'Weekly (Accelerated)': 52,
    'Bi-Weekly': 26,
    'Bi-Weekly (Accelerated)': 26,
    'Semi-Monthly': 24,
    'Monthly': 12
};

class Summary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            teamMemberData: null,
            teamMember: null,
            teamMemberLoading: false,
            teamMemberError: null,
            gravityFormSubmitting: false,
            gravityFormSuccess: false,
            gravityFormError: null,
            bookAMeetingClicked: false,
            callNowButtonClicked: false
        };
        this.abortController = new AbortController();
    }

    async componentDidMount() {
        try {
            await this.fetchTeamMember();
            this.submitCalculatorDraft();
        } catch (error) {
            logger.error('Error during MortgageSummary componentDidMount', {
                component: 'MortgageSummary',
                error: error.message,
                stack: error.stack
            });
        }
    }

    componentWillUnmount() {
        // Cancel any ongoing async operations
        this.abortController.abort();
    }

    componentDidUpdate(prevProps, prevState) {
        // Watch for changes in form values and auto-update the draft
        const formValues = [
            'annualIncome', 'monthlyDebt', 'downPayment', 'amortizationPeriod',
            'mortgageTerm', 'mortgageRate', 'closingCost', 'paymentFrequency',
            'mortgageCostDisplay', 'mortgagePaymentDisplay', 'postalCode'
        ];

        let hasFormValueChanged = false;

        hasFormValueChanged = formValues.some(name =>
            prevProps[name] !== this.props[name]
        );

        // Check if teamMember has changed
        const hasTeamMemberChanged = prevState.teamMember !== this.state.teamMember;
        const hasBookAMeetingClickedChanged = prevState.bookAMeetingClicked !== this.state.bookAMeetingClicked;
        const hasCallNowButtonClickedChanged = prevState.callNowButtonClicked !== this.state.callNowButtonClicked;

        // Only resubmit if form values or teamMember changed and we're not already submitting
        if ((hasFormValueChanged || hasTeamMemberChanged || hasBookAMeetingClickedChanged || hasCallNowButtonClickedChanged) && !this.state.gravityFormSubmitting && this.props.submissionId) {
            this.submitCalculatorDraft();
        }
    }

    fetchTeamMember = async () => {
        this.setState({ teamMemberLoading: true, teamMemberError: null });

        const {
            postalCode
        } = this.props;

        // trim all whitespace and convert to uppercase to match expected format
        const formattedPostalCode = postalCode.replace(/\s/g, '').toUpperCase();
        const headers = {
            'Content-Type': 'application/json'
        };
        const apiUrl = `${getTeamMember}?postal_code=${formattedPostalCode}`;

        try {
            // Get token if not available
            let { token } = this.props;
            if (!token) {
                const tokenAction = await this.props.dispatch(getToken());
                token = tokenAction.value;
            }

            // Add Authorization header if token is available
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(apiUrl, {
                headers,
                signal: this.abortController.signal
            });

            logger.logApiCall(
                'GET',
                apiUrl,
                { postal_code: formattedPostalCode },
                response,
                null
            );

            if (!response.ok) {
                logger.error('Team member API request failed', {
                    url: apiUrl,
                    status: response.status,
                    statusText: response.statusText
                });
                throw new Error(`Failed to fetch team member: ${response.status} ${response.statusText}`);
            }

            const teamMemberData = await response.json();

            logger.info('Team member data retrieved successfully', {
                component: 'MortgageSummary',
                hasTeamMember: !!(teamMemberData && teamMemberData.team_member),
                teamMemberName: teamMemberData.team_member ? teamMemberData.team_member.name : 'N/A',
                teamMemberData: teamMemberData
            });

            this.setState({ teamMemberData: teamMemberData, teamMemberLoading: false });
            if (teamMemberData && teamMemberData.team_member) {
                this.setState({ teamMember: teamMemberData.team_member });
            }
        } catch (error) {
            // Don't update state if request was aborted (component unmounted)
            if (error.name === 'AbortError') {
                logger.info('Team member fetch aborted - component unmounted');
                return;
            }

            logger.logApiCall(
                'GET',
                apiUrl,
                { postal_code: formattedPostalCode },
                null,
                error
            );

            this.setState({
                teamMemberError: error.message,
                teamMemberLoading: false
            });
        }
    };

    handleBookMeetingClick = async () => {
        // Check if component is still mounted before updating state
        if (this.abortController.signal.aborted) {
            return;
        }

        logger.logUserAction('Book Meeting Clicked', 'MortgageSummary', {
            teamMemberName: this.state.teamMember ? this.state.teamMember.name : 'N/A',
            postalCode: this.props.postalCode
        });

        trackingEvent({
            eventAction: 'Mortgage Calculator - Start Planning',
            eventLabel: 'Start planning your mortgage today'
        });

        // Set the meeting clicked state
        this.setState({ bookAMeetingClicked: true });
    };

    handleCallNowButtonClick = async () => {
        // Check if component is still mounted before updating state
        if (this.abortController.signal.aborted) {
            return;
        }

        logger.logUserAction('Call Now Clicked', 'MortgageSummary', {
            teamMemberName: this.state.teamMember ? this.state.teamMember.name : 'N/A',
            phoneNumber: ( this.state.teamMember && this.state.teamMember.phone_number ) ? this.state.teamMember.phone_number : callButton.link,
            postalCode: this.props.postalCode
        });

        trackingEvent({
            eventAction: 'Mortgage Calculator - Call Now',
            eventLabel: 'Call a Libro Coach'
        });

        // Set the call now button clicked state
        this.setState({ callNowButtonClicked: true });
    };

    submitCalculatorDraft = async () => {
        // Check if component is still mounted before updating state
        if (this.abortController.signal.aborted) {
            return;
        }

        // Prevent duplicate submissions
        if (this.state.gravityFormSubmitting) {
            logger.warn('Form submission already in progress, skipping');
            return;
        }

        this.setState({
            gravityFormSubmitting: true,
            gravityFormSuccess: false,
            gravityFormError: null,
        });

        const {
            annualIncome,
            monthlyDebt,
            downPayment,
            amortizationPeriod,
            mortgageTerm,
            mortgageRate,
            closingCost,
            paymentFrequency,
            mortgageCostDisplay,
            mortgagePaymentDisplay,
            postalCode,
        } = this.props;

        const { teamMember } = this.state;

        // Log calculation inputs
        logger.logCalculation('mortgage', {
            annualIncome,
            monthlyDebt,
            downPayment,
            amortizationPeriod,
            mortgageTerm,
            mortgageRate,
            closingCost,
            paymentFrequency,
            postalCode
        }, {
            mortgageCostDisplay,
            mortgagePaymentDisplay
        });

        // Only submit if we have the necessary data to avoid creating empty drafts.
        if ( annualIncome > 0 && downPayment > 0 && mortgageRate > 0 ) {
            logger.info('Starting mortgage calculator form submission', {
                component: 'MortgageSummary',
                submissionId: this.props.submissionId,
                isUpdate: !!this.props.submissionId,
                teamMember: teamMember && teamMember.name ? teamMember.name : 'N/A',
            });

            try {
                // Get token if not available
                let { token } = this.props;
                if (!token) {
                    const tokenAction = await this.props.dispatch(getToken());
                    token = tokenAction.value;
                }

                // Parse the display values back to numbers
                const maxPurchasePrice = parseFloat(mortgageCostDisplay.replace(/[$,]/g, '')) || 0;
                const estimatedPayment = parseFloat(mortgagePaymentDisplay.replace(/[$,]/g, '')) || 0;

                const phoneNumber = teamMember && teamMember.phone_number ? teamMember.phone_number : callButton.link;

                const submissionData = {
                    calculator_type: 'mortgage',
                    form_values: {
                        annualIncome: annualIncome,
                        monthlyDebt: monthlyDebt,
                        downPayment: downPayment,
                        amortizationPeriod: amortizationPeriod,
                        paymentFrequency: paymentFrequency,
                        mortgageTerm: mortgageTerm,
                        mortgageRate: mortgageRate,
                        closingCost: closingCost,
                        maxPurchasePrice: maxPurchasePrice,
                        estimatedPayment: estimatedPayment,
                        libroCoach: teamMember ? teamMember.name : 'N/A',
                        libroCoachEmail: teamMember ? teamMember.email : 'N/A',
                        postalCode: postalCode,
                        bookAMeetingLink: teamMember && teamMember.booking_url ? teamMember.booking_url : ctaButton.link,
                        bookAMeetingClicked: this.state.bookAMeetingClicked,
                        callNowPhoneNumber: phoneNumber.replace(/^tel:/, ''),
                        callNowButtonClicked: this.state.callNowButtonClicked,
                    }
                };

                // Include submission_id if we have one (for updates)
                if (this.props.submissionId) {
                    submissionData.submission_id = this.props.submissionId;
                }

                const action = mortgageSubmission(token, submissionData);
                const response = await action.promise;
                const result = response.data;

                if (result.success) {
                    logger.logFormSubmission('MortgageCalculatorDraft', submissionData, true);

                    // Store submission ID in Redux for persistence across navigation
                    this.props.dispatch(setMortgageSubmissionId(result.submission_id));
                    this.setState({
                        gravityFormSuccess: true,
                        gravityFormSubmitting: false
                    });
                } else {
                    throw new Error(result.errors || 'Submission failed');
                }
            } catch (error) {
                // Don't update state if request was aborted (component unmounted)
                if (error.name === 'AbortError') {
                    return;
                }

                logger.logFormSubmission('MortgageCalculatorDraft', {
                    annualIncome,
                    monthlyDebt,
                    downPayment,
                    postalCode,
                    teamMember: teamMember && teamMember.name ? teamMember.name : 'N/A'
                }, false, error);

                console.error('Error submitting to Gravity Form:', error);
                this.setState({
                    gravityFormError: error.message,
                    gravityFormSubmitting: false
                });
            }
        } else {
            logger.info('Skipping form submission - insufficient data', {
                component: 'MortgageSummary',
                hasAnnualIncome: annualIncome > 0,
                hasDownPayment: downPayment > 0,
                hasMortgageRate: mortgageRate > 0
            });
        }
    };

    render() {
        const {
            mortgageCostDisplay,
            mortgagePaymentDisplay,
            paymentFrequency,
            paymentSuffix,
            annualIncome,
            monthlyDebt,
            downPayment,
            amortizationPeriod,
            mortgageTerm,
            mortgageRate,
            closingCost,
            estimatedBudget,
            mortgageTermOptions,
            mortgageTerms,
            dispatch
        } = this.props;

        const { teamMember, teamMemberLoading, teamMemberError, gravityFormError } = this.state;

        let coachCaption = 'Libro Coach, ';
        let coachImage = defaultCoachImage; // default image if team member doesn't have one
        let coachName = 'Casey Fraser'; // default name if team member doesn't have one

        // if team member data is available, update the ctaButton link to include the team member's booking URL
        if ( teamMember ) {
            if ( teamMember.booking_url ) {
                ctaButton.link = teamMember.booking_url;
            }
            if ( teamMember.profile_image ) {
                coachImage = teamMember.profile_image;
                if ( teamMember.name ) {
                    coachName = teamMember.name;
                }
            }
            if (teamMember.phone_number) {
                let phoneNumber = teamMember.phone_number.replace(/\D/g, ''); // Remove non-numeric characters
                if (phoneNumber.length === 10) {
                    phoneNumber = '1' + phoneNumber; // Add country code if missing
                }
                callButton.link = `tel:${phoneNumber}`;
            }
        }
        // add the coach's name to the caption
        coachCaption += coachName;

        // Append UTM params to the booking link so the Coconut webhook can
        // correlate the resulting booking back to this calculator submission (#180).
        // utm_campaign routes the webhook by calculator type; utm_content carries
        // the staging row id (submissionId).
        const bookingLink = appendQueryParams(ctaButton.link, {
            utm_campaign: 'mortgage_calculator',
            utm_content: this.props.submissionId,
        });
        const bookingButton = { ...tooltip.button, link: bookingLink };

        return (
            <div className={styles.page}>
                <div className={styles.headerBg}>
                    <div className={styles.headerInner}>
                        <div className={styles.headerLeft}>
                            <h1 className={styles.mainTitle}>Affordability Summary</h1>
                            <p className={styles.summary}>
                                {'Great news! Based on your selections, your estimated mortgage would be '}
                                <strong>{mortgageCostDisplay}</strong>
                                {' and your '}
                                <strong>{paymentFrequency || 'Monthly'} Payment</strong>
                                {' would be '}
                                <strong>{mortgagePaymentDisplay}</strong>.
                            </p>
                        </div>
                        <div className={styles.headerRight}>
                            <div className={styles.statItem}>
                                <span className={styles.statLabelRow}>
                                    <span className={styles.homeIcon} />
                                    <span className={styles.statLabel}>Maximum Purchase Price</span>
                                </span>
                                <span className={styles.statValue}>{mortgageCostDisplay}</span>
                            </div>
                            <div className={styles.statItem}>
                                <span className={styles.statLabelRow}>
                                    <span className={styles.calendarIcon} />
                                    <span className={styles.statLabel}>{paymentFrequency || 'Monthly'} Payment</span>
                                </span>
                                <span className={styles.statValue}>{mortgagePaymentDisplay}<span className={styles.statSuffix}>/{paymentSuffix}</span></span>
                            </div>
                            <p className={styles.headerNote}>*This tool provides an estimated affordability range based on the information you enter. Mortgage approval and final amounts are subject to a full financial assessment.</p>
                        </div>
                    </div>

                    <div className={styles.buttons}>
                        <a
                            href={bookingLink}
                            onClick={this.handleBookMeetingClick}
                            className={styles.ctaButton}
                            title={ctaButton.label}
                            target={ctaButton.target}
                            rel='noopener noreferrer'
                        >
                            {ctaButton.label}
                        </a>
                        <Link
                            to={`${process.env.PUBLIC_URL}/mortgage${Routes.STEPNINE}`}
                            className={styles.outlineButton}
                            title='Go back to make changes'
                        >
                            Go Back to Make Changes
                        </Link>

                        {/* Status messages for Gravity Form submission */}
                        {gravityFormError && (
                            <div style={{
                                marginTop: '1rem',
                                padding: '0.75rem',
                                backgroundColor: '#f8d7da',
                                color: '#721c24',
                                borderRadius: '4px',
                                border: '1px solid #f5c6cb'
                            }}>
                                <strong>Submission Error:</strong> {gravityFormError}
                            </div>
                        )}
                    </div>
                </div>

                {teamMemberError && (
                    <p style={{ color: '#ee2e24' }}>Unable to load coach information: {teamMemberError}</p>
                )}

                { teamMember && (
                    <div className={styles.coachSection}>
                        <div className={styles.coachInner}>
                            <div className={styles.coachImageWrap}>
                                <img src={coachImage} alt={coachCaption} className={styles.coachImage} />
                                <div className={styles.coachCaption}><p>{coachCaption}</p></div>
                            </div>
                            <div className={styles.coachContent}>
                                <h2 className={styles.coachTitle}>{"What's your next move?"}</h2>
                                <p className={styles.coachDesc}>
                                    {"Connect with a Libro Coach to lock in your down payment strategy, reduce costs, or explore refinancing\u2014whatever gets you home faster."}
                                </p>

                                {teamMemberLoading && (
                                    <p>Loading your local Libro Coach...</p>
                                )}

                                <ul className={styles.coachChecklist}>
                                    <li className={styles.checklistItem}>
                                        <span className={styles.checkGreen} />
                                        Explore mortgage affordability
                                    </li>
                                    <li className={styles.checklistItem}>
                                        <span className={styles.checkBlue} />
                                        Get expert advice from a Libro Coach
                                    </li>
                                    <li className={styles.checklistItem}>
                                        <span className={styles.checkBlue} />
                                        Get Pre-approved
                                    </li>
                                </ul>
                                <div>
                                <a
                                    href={bookingLink}
                                    onClick={this.handleBookMeetingClick}
                                    className={styles.coachButton}
                                    title={ctaButton.label}
                                    target={ctaButton.target}
                                    rel='noopener noreferrer'
                                    >
                                    {ctaButton.label}
                                </a>
                                <a
                                    href={callButton.link}
                                    onClick={this.handleCallNowButtonClick}
                                    className={styles.coachCallLink}
                                    title={callButton.label}
                                    target={callButton.target}
                                    >
                                    {callButton.label}
                                </a>
                                </div>
                                <p className={styles.coachSubtext}>Reserve in minutes. Meet by phone, online, or face-to-face.</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.answersSection}>
                    <div className={styles.content}>
                        <SummaryAnswers
                            annualIncome={annualIncome}
                            monthlyDebt={monthlyDebt}
                            downPayment={downPayment}
                            amortizationPeriod={amortizationPeriod}
                            paymentFrequency={paymentFrequency}
                            mortgageTerm={mortgageTerm}
                            mortgageRate={mortgageRate}
                            closingCost={closingCost}
                            estimatedBudget={estimatedBudget}
                            mortgageTermOptions={mortgageTermOptions}
                            mortgageTerms={mortgageTerms}
                            paymentFrequencyNumMap={paymentFrequencyNumMap}
                            updateField={(form, field, value) => dispatch(change(form, field, value))}
                        />

                        <div className={styles.tipsWrap}>
                            <InfoBox
                                title={tooltip.title}
                                content={tooltip.content}
                                button={bookingButton}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const StepOneForm = formValueSelector(Routes.STEPONE);
const StepTwoForm = formValueSelector(Routes.STEPTWO);
const StepFourForm = formValueSelector(Routes.STEPFOUR);
const StepFiveForm = formValueSelector(Routes.STEPFIVE);
const StepSixForm = formValueSelector(Routes.STEPSIX);
const StepSevenForm = formValueSelector(Routes.STEPSEVEN);
const StepEightForm = formValueSelector(Routes.STEPEIGHT);
const StepNineForm = formValueSelector(Routes.STEPNINE);

export default connect(
    state => {
        const annualIncome = float(StepOneForm(state, 'annualIncome')) || 0;
        const loans = float(StepTwoForm(state, 'loans')) || 0;
        const carPayments = float(StepTwoForm(state, 'carPayments')) || 0;
        const linesOfCredit = float(StepTwoForm(state, 'linesOfCredit')) || 0;
        const creditCards = float(StepTwoForm(state, 'creditCards')) || 0;
        const other = float(StepTwoForm(state, 'other')) || 0;
        const monthlyDebt = loans + carPayments + linesOfCredit + creditCards + other;
        const downPayment = float(StepFourForm(state, 'downPayment')) || 0;
        const amortizationPeriod = float(StepFiveForm(state, 'amortizationPeriod')) || 25;
        const paymentFrequency = StepSixForm(state, 'paymentFrequency') || 'Monthly';
        const paymentFrequencyNum = float(StepSixForm(state, 'paymentFrequencyNum')) || 12;
        const mortgageTerm = StepSevenForm(state, 'mortgageTerm') || '';
        const mortgageRate = float(StepEightForm(state, 'mortgageRate')) || 0;
        const closingCost = float(StepNineForm(state, 'closingCost')) || 0;
        const postalCode = StepNineForm(state, 'postalCode') || '';

        const mortgageTerms = state.mortgage.terms || [];
        const mortgageTermOptions = mortgageTerms.map(({ Range, SubType }) => formatMortgageTerm(Range, SubType));

        // Affordability calculation
        const monthlyIncome = annualIncome / 12;
        const maxMonthlyPayment = Math.max((monthlyIncome * 0.32) - monthlyDebt, 0);
        const maxMortgage = calculateMaxMortgage(maxMonthlyPayment, amortizationPeriod, mortgageRate);
        let maxPurchasePrice = Math.max(Math.round(maxMortgage), 0);
        const mortgageMin = 100; // Set a minimum mortgage amount to avoid showing $0 for very low affordability
        if ( maxMortgage > mortgageMin ) {
            maxPurchasePrice = Math.max(Math.round(maxMortgage + downPayment), 0);
        } else {
            logger.info('Calculated max purchase price is below the minimum, setting to $0', {
                maxMortgage,
                downPayment,
                maxPurchasePrice,
                mortgageMin
            });
            maxPurchasePrice = 0;
        }
        logger.info('Calculated max purchase price', {
            maxMortgage,
            downPayment,
            maxPurchasePrice
        });
        const mortgagePayment = maxMortgage > 0
            ? calculatePaymentFromPrincipal(maxMortgage, amortizationPeriod, paymentFrequencyNum, mortgageRate, paymentFrequency)
            : 0;
        logger.info('Calculated mortgage payment', {
            maxMortgage,
            mortgagePayment,
            maxPurchasePrice,
        });
        const estimatedBudget = Math.round(maxPurchasePrice - closingCost);
        logger.info('Calculated estimated budget', {
            maxPurchasePrice,
            closingCost,
            estimatedBudget
        });

        return {
            mortgageCostDisplay: maxPurchasePrice > 0 ? `$${numberWithCommas(maxPurchasePrice)}` : '$0',
            mortgagePaymentDisplay: mortgagePayment > 0 ? `$${numberWithCommas(Math.round(mortgagePayment))}` : '$0',
            paymentFrequency,
            paymentSuffix: frequencySuffixMap[paymentFrequency] || 'mo',
            annualIncome: Math.round(annualIncome),
            monthlyDebt: Math.round(monthlyDebt),
            downPayment: Math.round(downPayment),
            amortizationPeriod,
            mortgageTerm,
            mortgageRate,
            closingCost: Math.round(closingCost),
            estimatedBudget,
            mortgageTermOptions,
            mortgageTerms,
            submissionId: state.mortgage.submissionId || null,
            token: state.auth.token,
            postalCode
        };
    }
)(Summary);
